import React, { memo } from 'react'
import { Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native'
import { useRouter } from 'expo-router'
import colors from '@/constants/Colors'
import { useSupabase } from '@/hooks/useSupabase'
import { isOneSignalAvailable, isPushAccountLoginPending, isPushLinkedToAccount } from '@/lib/onesignal-push'
import {
    serverStatusIsError,
    serverStatusLabel,
    usePushRegistration,
    type ServerRegistrationStatus,
} from '@/context/PushRegistrationProvider'

function NotificationStatusInner() {
    const router = useRouter()
    const { user } = useSupabase()
    const {
        localState: state,
        serverStatus,
        locallyHealthy,
        isFullyHealthy,
        isHealing,
        lastHealedAt,
        heal,
    } = usePushRegistration()

    if (Platform.OS === 'web' || !isOneSignalAvailable()) return null

    const { permission, optedIn, subscriptionId, pushToken, externalId } = state
    const isLinked = user?.id ? isPushLinkedToAccount(state, user.id) : Boolean(externalId)
    const loginPending = user?.id ? isPushAccountLoginPending(state, user.id) : false
    const deviceRegisteredOnServer = serverStatus === 'registered' && Boolean(subscriptionId && pushToken)
    const awaitingFirstCheck = serverStatus === 'idle' && lastHealedAt === null
    const displayHealthy = isFullyHealthy || (locallyHealthy && awaitingFirstCheck)
    const looksEnabledButUnregistered = permission && optedIn && !subscriptionId
    const looksRegisteredButUnlinked = Boolean(subscriptionId) && !isLinked
    const missingPushToken = Boolean(subscriptionId) && !pushToken

    const handleOpenGuide = () => {
        try { router.push('/enable-notifications') } catch { }
    }

    const handleRetryServerSync = () => {
        void heal({ forceServerSync: true })
    }

    return (
        <View style={styles.container}>
            <Text style={styles.heading}>Push Notifications</Text>

            {isHealing && (
                <Text style={styles.healingBanner}>Registering this device…</Text>
            )}

            <View style={styles.infoRow}>
                <Text style={styles.label}>System permission:</Text>
                <Text style={[styles.value, permission ? styles.ok : styles.bad]}>
                    {permission ? 'Allowed' : 'Blocked'}
                </Text>
            </View>

            <View style={styles.infoRow}>
                <Text style={styles.label}>Push subscription:</Text>
                <Text style={[styles.value, locallyHealthy ? styles.ok : styles.bad]}>
                    {locallyHealthy
                        ? 'Active'
                        : optedIn && subscriptionId
                            ? 'Not linked'
                            : optedIn
                                ? 'Registering…'
                                : 'Not active'}
                </Text>
            </View>

            <View style={styles.infoRow}>
                <Text style={styles.label}>Linked account:</Text>
                <Text style={[styles.value, isLinked ? styles.ok : loginPending ? styles.pending : styles.bad]} numberOfLines={1} selectable>
                    {isLinked ? 'Yes' : loginPending ? 'Pending…' : externalId ? 'Wrong account' : 'No'}
                </Text>
            </View>

            <View style={styles.infoRow}>
                <Text style={styles.label}>Registered on server:</Text>
                <Text style={[styles.value, serverStatusStyle(serverStatus, awaitingFirstCheck, deviceRegisteredOnServer)]}>
                    {deviceRegisteredOnServer
                        ? 'Yes'
                        : awaitingFirstCheck
                            ? 'Checking…'
                            : serverStatusLabel(serverStatus)}
                </Text>
            </View>

            <View style={styles.infoRow}>
                <Text style={styles.label}>Subscription ID:</Text>
                <Text style={[styles.value, styles.mono, subscriptionId ? styles.ok : styles.bad]} numberOfLines={1} selectable>
                    {subscriptionId ? subscriptionId.substring(0, 8) + '…' : 'None'}
                </Text>
            </View>

            {serverStatusIsError(serverStatus) && pushToken && (
                <TouchableOpacity
                    style={styles.syncButton}
                    onPress={handleRetryServerSync}
                    disabled={isHealing}
                >
                    <Text style={styles.syncButtonText}>
                        {isHealing ? 'Registering with server…' : 'Retry server registration'}
                    </Text>
                </TouchableOpacity>
            )}

            <TouchableOpacity
                style={[styles.button, displayHealthy ? styles.buttonHealthy : styles.buttonAction]}
                onPress={handleOpenGuide}
            >
                <Text style={[styles.buttonText, displayHealthy && styles.buttonTextHealthy]}>
                    {displayHealthy ? 'Notification settings' : 'Fix notification issues'}
                </Text>
            </TouchableOpacity>

            {!displayHealthy && !isHealing && !awaitingFirstCheck && (
                <Text style={styles.helpText}>
                    {serverStatus === 'auth_error'
                        ? 'crew-api cannot authenticate with OneSignal. Check ONESIGNAL_API_KEY on crew-api matches office/supabase.'
                        : serverStatus === 'not_configured'
                            ? 'crew-api is missing OneSignal credentials. The app will keep retrying once they are configured.'
                            : serverStatus === 'unavailable'
                                ? 'Could not reach crew-api. The app will retry automatically in the background.'
                                : serverStatus === 'not_registered'
                                    ? 'This device is not on OneSignal yet. Registration retries automatically every 30 seconds.'
                                    : looksRegisteredButUnlinked
                                        ? 'This device has a subscription but it is not linked to your account yet.'
                                        : missingPushToken
                                            ? 'This device has not received a push token yet.'
                                            : looksEnabledButUnregistered
                                                ? 'Notifications look enabled but this device is not registered yet.'
                                                : 'Tap above if you need to enable notification permission.'}
                </Text>
            )}
        </View>
    )
}

function serverStatusStyle(
    status: ServerRegistrationStatus,
    awaitingFirstCheck: boolean,
    deviceRegisteredOnServer: boolean,
) {
    if (deviceRegisteredOnServer) return styles.ok
    if (awaitingFirstCheck || status === 'idle') return styles.pending
    return styles.bad
}

export const NotificationStatus = memo(NotificationStatusInner)

const styles = StyleSheet.create({
    container: {
        backgroundColor: '#fff',
        padding: 16,
        borderRadius: 10,
        marginBottom: 16,
    },
    heading: {
        fontSize: 16,
        fontWeight: '600',
        marginBottom: 12,
        color: '#222',
    },
    healingBanner: {
        fontSize: 12,
        color: colors.primary,
        marginBottom: 12,
        textAlign: 'center',
    },
    infoRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 8,
        borderBottomWidth: 1,
        borderBottomColor: '#f0f0f0',
    },
    label: {
        fontSize: 14,
        color: '#666',
        fontWeight: '500',
    },
    value: {
        fontSize: 14,
        color: '#222',
        fontWeight: '400',
        maxWidth: '60%',
        textAlign: 'right',
    },
    ok: {
        color: colors.success,
        fontWeight: '600',
    },
    bad: {
        color: colors.error,
        fontWeight: '600',
    },
    pending: {
        color: '#888',
        fontWeight: '600',
    },
    mono: {
        fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
    },
    syncButton: {
        marginTop: 12,
        paddingVertical: 10,
        paddingHorizontal: 16,
        borderRadius: 8,
        alignItems: 'center',
        borderWidth: 1,
        borderColor: colors.primary,
    },
    syncButtonText: {
        color: colors.primary,
        fontSize: 14,
        fontWeight: '600',
    },
    button: {
        marginTop: 16,
        paddingVertical: 12,
        paddingHorizontal: 24,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
    },
    buttonAction: {
        backgroundColor: colors.error,
    },
    buttonHealthy: {
        backgroundColor: '#f5f5f5',
        borderWidth: 1,
        borderColor: '#e0e0e0',
    },
    buttonText: {
        color: '#fff',
        fontSize: 16,
        fontWeight: '600',
    },
    buttonTextHealthy: {
        color: '#222',
    },
    helpText: {
        marginTop: 10,
        fontSize: 12,
        color: '#888',
        textAlign: 'center',
        lineHeight: 16,
    },
})
